"use client";

import { Fragment } from "react";
import Image from "next/image";
import Link from "next/link";
import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { PageCompetitionAffiliateSchema } from "@/sanity/pages/PageCompetitionAffiliateTemplate";
import { motion } from "framer-motion";

import bg from "@/public/global/bg-art.jpg";
import logo from "@/public/logo-black.svg";
import arrow from "@/public/global/dotted-arrow.svg";
import bannerDeco from "@/public/comp_affiliate/decoR.png";
import bannerDecoL from "@/public/comp_affiliate/decoL.png";
import Countdown from "@/components/global/countdown/Countdown";
import { cn } from "@/lib/utils";
import ChipSerif from "@/components/global/ui/ChipSerif";

interface HeroProps {
  data: PageCompetitionAffiliateSchema;
  className?: string;
  showTopBanner?: boolean;
}

export default function Hero({ data, className, showTopBanner }: HeroProps) {
  return (
    <section
      id="hero-section"
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
        showTopBanner && "mt-[16rem] lg:mt-[7rem]",
        className,
      )}
    >
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[5rem] pb-[3.25rem] lg:pb-[4.5rem]">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.1]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container flex relative z-10 flex-col items-center px-[1.5rem]">
          <div className="relative mx-auto w-full lg:px-0 lg:pt-0 max-w-[103rem] z-[1] pt-[2rem] mb-[3.25rem] lg:mb-[5.75rem]">
            <motion.div
              className="flex items-center w-[min(92.875rem,100%)] mx-auto mb-[2.85rem]"
              initial={{ opacity: 0, filter: "blur(10px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              transition={{
                duration: 0.3,
                delay: 0,
                ease: [0.25, 0.4, 0.25, 1],
              }}
            >
              <div className="w-[min(91.5rem,100%)] flex justify-between items-center lg:flex-row flex-col">
                <div className="flex gap-[2rem] items-center">
                  <Link href={"/"}>
                    <Image src={logo} alt="" className="w-[16.25rem]" />
                  </Link>

                  {data?.partnerName && (
                    <Fragment>
                      <Image src={arrow} alt="" className="flex-shrink-0" />
                      <div className="flex items-center gap-[1rem]">
                        <p className="text-[#103037] text-[2rem] lg:text-[2.25rem] font-[500] leading-[116%] m-0">
                          {data?.partnerName}
                        </p>
                      </div>
                    </Fragment>
                  )}
                </div>
                <div className="lg:mt-0 mt-[2rem]">
                  <p className="m-0 text-black/50 font-sans text-[1.75rem] font-[500] leading-[100%]">
                    {data?.dates.start} /{" "}
                    <em className="not-italic text-black/90">
                      {data?.dates.end}
                    </em>
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="w-[min(123.75rem,100%)] mx-auto relative"
              initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{
                duration: 0.3,
                delay: 0.1,
                ease: [0.25, 0.4, 0.25, 1],
              }}
            >
              <Image
                src={bannerDeco}
                alt=""
                className="absolute right-0 top-1/2 translate-x-[12%] -translate-y-1/2 z-[0] !h-[32.5rem] !w-[51rem] lg:block hidden"
              />

              <Image
                src={bannerDecoL}
                alt=""
                className="absolute left-0 top-1/2 -translate-x-[12%] -translate-y-1/2 z-[0] !h-[32.5rem] !w-[51rem] lg:block hidden"
              />
              {data?.banner && (
                <div className="w-[min(94.25rem,100%)] overflow-clip mx-auto lg:p-[0.75rem] rounded-[1.5rem] lg:rounded-[4.45rem] border border-[#DEDEDE]/20 bg-[#103037] backdrop-blur-[4rem] relative z-[1] ">
                  <Image
                    src={data?.banner}
                    alt=""
                    className="rounded-[0.75rem] lg:rounded-[3.75rem]"
                  />
                </div>
              )}
            </motion.div>
          </div>
          {data?.heroChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ChipSerif text={data.heroChip.text} />
            </motion.div>
          )}

          {data?.heroText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="richtext w-[min(92rem,100%)] prose-p:w-[min(64.75rem,90%)] prose-headings:text-[4.5rem] lg:prose-headings:text-[7rem]"
            >
              <PortableText
                value={data.heroText}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.heroCTA && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.5,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="w-full lg:w-fit"
            >
              <CTAButton
                className="mt-[3rem] lg:min-w-[60.125rem]"
                data={data.heroCTA}
              />
            </motion.div>
          )}

          {data.toggleCountdown && (
            <div className="mt-[2.5rem] w-[min(60.125rem,100%)] mx-auto rounded-[1.5rem] lg:rounded-[2.25rem] backdrop-blur-[5.375rem] bg-[#1030378C]">
              <Countdown
                dateTimeString={data?.countdown}
                lang={data.countdownTextLang ?? "de"}
              />
            </div>
          )}

          <div className="w-[min(138.375rem,100%)] mx-auto flex justify-center items-center lg:mt-[8rem] lg:flex-row flex-col mt-[4rem]">
            {data?.heroItems.map((item, index) => (
              <Fragment key={index}>
                <motion.div
                  className="flex items-center gap-[1.5rem] py-[0.5rem] pl-[.5rem] pr-[2rem]"
                  initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{
                    duration: 0.3,
                    delay: 0.3 + index * 0.1,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                >
                  {item.icon && <Image src={item.icon} alt="" />}
                  <p className="m-0 text-[#103037] font-[500] leading-[140%] text-[2rem]">
                    {item.text}
                  </p>
                </motion.div>

                {index < data.heroItems.length - 1 && (
                  <div className="w-full max-w-[95%] bg-black/10 mx-[1rem] h-[.125rem] lg:h-[4rem] lg:w-[.125rem] my-[1rem] lg:my-0" />
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
